import Conditions from '../../../../../resources/conditions';
import Outputs from '../../../../../resources/outputs';
import { callOverlayHandler } from '../../../../../resources/overlay_plugin_api';
import { Responses } from '../../../../../resources/responses';
import { NamedConfigEntry } from '../../../../../resources/user_config';
import Util, { DirectionOutputCardinal, Directions } from '../../../../../resources/util';
import ZoneId from '../../../../../resources/zone_id';
import { RaidbossData } from '../../../../../types/data';
import { PluginCombatantState } from '../../../../../types/event';
import { NetMatches } from '../../../../../types/net_matches';
import { TriggerSet } from '../../../../../types/trigger';

const centerX = 100;
const centerY = 100;

// Using the "from" point, returns the heading (in radians) between the
// negative (south) y-axis and the ray to the "to" point.
const getRelativeHdg = (toX: number, toY: number, fromX: number, fromY: number): number => {
  const deltaX = toX - fromX;
  const deltaY = toY - fromY;
  return Math.atan2(deltaX, deltaY);
};

// Titan will always jump to one of four cardinal spots. The opposite cardinal is safe.
type JumpSite = {
  jump: DirectionOutputCardinal;
  x: number;
  y: number;
  safe: DirectionOutputCardinal;
};
const jumpSites: JumpSite[] = [
  { jump: 'dirW', x: 86.0, y: 100.0, safe: 'dirE' },
  { jump: 'dirE', x: 114.0, y: 100.0, safe: 'dirW' },
  { jump: 'dirN', x: 100.0, y: 86.0, safe: 'dirS' },
  { jump: 'dirS', x: 100.0, y: 114.0, safe: 'dirN' },
];

type BossKey = 'garuda' | 'ifrit' | 'titan' | 'ultima';

export interface Data extends RaidbossData {
  readonly triggerSetConfig: {
    gaolOrder1: string;
    gaolOrder2: string;
    gaolOrder3: string;
    gaolOrder4: string;
    gaolOrder5: string;
    gaolOrder6: string;
    gaolOrder7: string;
    gaolOrder8: string;
    gaolOrder9: string;
    gaolOrder10: string;
    gaolOrder11: string;
    gaolOrder12: string;
    gaolOrder13: string;
    gaolOrder14: string;
    gaolOrder15: string;
    gaolOrder16: string;
    gaolOrder17: string;
    gaolOrder18: string;
    gaolOrder19: string;
    gaolOrder20: string;
  };
  combatantData: PluginCombatantState[];
  phase:
    | 'garuda'
    | 'ifrit'
    | 'titan'
    | 'intermission'
    | 'predation'
    | 'annihilation'
    | 'suppression'
    | 'finale';
  bossId: { [name in BossKey]?: string };
  garudaAwoken: boolean;
  ifritAwoken: boolean;
  thermalLow: { [name: string]: number };
  beyondLimits: Set<string>;
  slipstreamCount: number;
  nailAdds: NetMatches['AddedCombatant'][];
  nailDeaths: { [name: string]: NetMatches['Ability'] };
  nailDeathOrder: string[];
  nailDeathFirst8Dir?: number;
  nailDeathLast8Dir?: number;
  nailDeathRotationDir?: 'cw' | 'ccw';
  ifritUntargetableCount: number;
  seenTitanFirstJump: boolean;
  titanGaols: string[];
  seenTitanGaols?: boolean;
  lastTitanMove?: NetMatches['ActorMove'];
  titanBury: NetMatches['AddedCombatant'][];
  ifritRadiantPlumeLocations: DirectionOutputCardinal[];
  possibleIfritIDs: string[];
}

type GaolKey = Extract<keyof Data['triggerSetConfig'], string>;
const gaolConfig = (id: GaolKey): NamedConfigEntry<GaolKey> => {
  // Since these are all explicit string types, get the number from the string.
  const numStr = id.replace('gaolOrder', '');
  return {
    id: id,
    name: {
      en: `Titan Gaol Order ${numStr}`,
      de: `Titan Gefängnis Reihenfolge ${numStr}`,
      fr: `Ordre geôle de Titan ${numStr}`,
      ja: `ジェイルの順番 ${numStr}`,
      cn: `泰坦石牢顺序 ${numStr}`,
      ko: `돌감옥 순서 ${numStr}`,
      tc: `泰坦石牢順序 ${numStr}`,
    },
    type: 'string',
    default: '',
  };
};

// Ultima Weapon Ultimate
const triggerSet: TriggerSet<Data> = {
  id: 'TheWeaponsRefrainUltimate',
  zoneId: ZoneId.TheWeaponsRefrainUltimate,
  config: [
    // Yes yes, a textarea would be nice here to put everything on separate lines,
    // but OverlayPlugin does not seem to support delivering the enter key and
    // so there's no way to have one box with names on separate lines.  Sorry!
    /* eslint-disable max-len */
    {
      ...gaolConfig('gaolOrder1'),
      comment: {
        en:
          'Each entry can be the three letter job (e.g. "war" or "SGE") or the full name (e.g. "Tini Poutini"), all case insensitive. Smaller numbers will be listed first in the gaol order. Duplicate jobs will sort players alphabetically. Anybody not listed will be added to the end alphabetically. Blank entries are ignored. If players are listed multiple times by name or job, the lower number will be considered.',
        de:
          'Jeder Eintrag kann aus drei Buchstaben des Jobs bestehen (z. B. "war" oder "SGE") oder aus dem vollständigen Namen (z. B. "Tini Poutini"), wobei Groß- und Kleinschreibung nicht berücksichtigt werden. Kleinere Nummern werden in der Reihenfolge der Gefängnisse zuerst aufgeführt. Bei doppelten Aufträgen werden die Spieler alphabetisch sortiert. Jeder nicht aufgeführte Spieler wird am Ende alphabetisch eingeordnet. Leere Einträge werden ignoriert. Wenn Spieler mehrfach nach Namen oder Beruf aufgelistet sind, wird die niedrigere Nummer berücksichtigt.',
        fr:
          'Chaque entrée peut être désigné par les jobs en trois lettres (par exemple "war" ou "SGE") ou le nom complet (par exemple "Tini Poutini"), sans tenir compte des majuscules et minuscules. Les plus petits numéros seront classés en premier dans l\'ordre des geôles. Les doublons seront classés par ordre alphabétique. Toute personne ne figurant pas sur la liste sera ajoutée à la fin par ordre alphabétique. Les entrées vides sont ignorées. Si des joueurs sont listés plusieurs fois par nom ou par fonction, le numéro le plus bas sera pris en compte.',
        ja:
          '各項目は、3文字のジョブ名（例: "war" または "SGE"）またはフルネーム（例: "Tini Poutini"）のいずれかを入力できます。大文字小文字は区別されません。番号の小さい順にジェイルの順番リストに登録されます。重複するジョブは名前順に並べ替えられます。リストされていないプレイヤーは名前順に最後に追加されます。空白の項目は無視されます。プレイヤーが名前またはジョブで複数回登録されている場合、小さいほうの番号が使用されます。',
        cn:
          '每个条目可以是三个字母的职业缩写 (例如 "war" 或  "SGE") 或玩家全名（例如 "Tini Poutini"），所有字母不区分大小写。编号较小的将在石牢顺序中排列在前。重复的职业将按姓名字母顺序对玩家进行排序。未列出的队员将按字母顺序添加到末尾。空白条目将被忽略。如果玩家按姓名或职业被多次列出，则以较小编号为准。',
        ko:
          '각 항목에는 대소문자를 구분하지 않는 세 글자 직업명(예: "war" 또는 "SGE") 또는 전체 이름(예: "빛의전사")을 입력할 수 있습니다. 먼저 입력된 항목이 감옥 순서에서 먼저 나열됩니다. 직업이 중복된 경우에는  알파벳 순(가나다 순)으로 나타납니다. 목록에 없는 사람은 알파벳 순으로 맨 끝에 추가됩니다. 빈 칸은 무시됩니다. 플레이어가 이름 또는 직업별로 여러 번 나열된 경우, 먼저 입력된 항목이 사용됩니다.',
        tc:
          '每個條目可以是三個字母的職業縮寫 (例如 "war" 或  "SGE") 或玩家全名（例如 "Tini Poutini"），所有字母不區分大小寫。編號較小的將在石牢順序中排列在前。重複的職業將按姓名字母順序對玩家進行排序。未列出的隊員將按字母順序添加到末尾。空白條目將被忽略。如果玩家按姓名或職業被多次列出，則以較小編號為準。',
      },
    },
    gaolConfig('gaolOrder2'),
    gaolConfig('gaolOrder3'),
    gaolConfig('gaolOrder4'),
    gaolConfig('gaolOrder5'),
    gaolConfig('gaolOrder6'),
    gaolConfig('gaolOrder7'),
    gaolConfig('gaolOrder8'),
    gaolConfig('gaolOrder9'),
    gaolConfig('gaolOrder10'),
    gaolConfig('gaolOrder11'),
    gaolConfig('gaolOrder12'),
    gaolConfig('gaolOrder13'),
    gaolConfig('gaolOrder14'),
    gaolConfig('gaolOrder15'),
    gaolConfig('gaolOrder16'),
    gaolConfig('gaolOrder17'),
    gaolConfig('gaolOrder18'),
    gaolConfig('gaolOrder19'),
    gaolConfig('gaolOrder20'),
    /* eslint-enable max-len */
  ],
  timelineFile: 'ultima_weapon_ultimate.txt',
  initData: () => {
    return {
      combatantData: [],
      phase: 'garuda',
      bossId: {},
      garudaAwoken: false,
      ifritAwoken: false,
      thermalLow: {},
      beyondLimits: new Set<string>(),
      slipstreamCount: 0,
      nailAdds: [],
      nailDeaths: {},
      nailDeathOrder: [],
      ifritUntargetableCount: 0,
      seenTitanFirstJump: false,
      titanGaols: [],
      titanBury: [],
      ifritRadiantPlumeLocations: [],
      possibleIfritIDs: [],
    };
  },
  timelineTriggers: [
    {
      id: 'UWU Diffractive Laser',
      regex: /Diffractive Laser/,
      beforeSeconds: 5,
      suppressSeconds: 3,
      response: Responses.tankCleave(),
    },
    {
      id: 'UWU Feather Rain',
      regex: /Feather Rain/,
      beforeSeconds: 3,
      suppressSeconds: 3,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Move!',
          de: 'Bewegen',
          fr: 'Bougez !',
          ja: 'フェザーレイン',
          cn: '躲羽毛',
          ko: '이동',
          tc: '躲羽毛',
        },
      },
    },
    {
      id: 'UWU Eruption',
      regex: /Eruption 1/,
      beforeSeconds: 10,
      condition: (data) => data.phase !== 'suppression',
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Eruption Baits',
          de: 'Köder Eruption',
          fr: 'Attirez les éruptions',
          ja: 'エラプション',
          cn: '诱导地火',
          ko: '용암 분출 유도',
          tc: '誘導噴發',
        },
      },
    },
  ],
  triggers: [
    // --------- Phases & Buff Tracking ----------
    {
      id: 'UWU Phase Tracker',
      type: 'Ability',
      // 2B53 = Slipstream
      // 2B5F = Crimson Cyclone
      // 2CFD = Geocrush
      // 2CF5 = Intermission
      // 2B87 = Tank Purge
      // 2D4C = Ultimate Annihilation
      // 2D4D = Ultimate Suppression
      netRegex: { id: ['2B53', '2B5F', '2CFD', '2CF5', '2B87', '2D4C', '2D4D'] },
      run: (data, matches) => {
        if (data.phase === 'garuda' && matches.id === '2B53') {
          data.bossId.garuda = matches.sourceId;
        } else if (data.phase === 'garuda' && matches.id === '2B5F') {
          data.phase = 'ifrit';
          data.bossId.ifrit = matches.sourceId;
        } else if (data.phase === 'ifrit' && matches.id === '2CFD') {
          data.phase = 'titan';
          data.bossId.titan = matches.sourceId;
        } else if (data.phase === 'titan' && matches.id === '2CF5') {
          data.phase = 'intermission';
        } else if (data.phase === 'intermission' && matches.id === '2B87') {
          data.phase = 'predation';
          data.bossId.ultima = matches.sourceId;
        } else if (matches.id === '2D4C') {
          data.phase = 'annihilation';
        } else if (matches.id === '2D4D') {
          data.phase = 'suppression';
        }
      },
    },
    {
      // Wait after suppression for primal triggers at the end.
      id: 'UWU Phase Tracker Finale',
      type: 'Ability',
      netRegex: { source: 'The Ultima Weapon', id: '2D4D', capture: false },
      delaySeconds: 74,
      run: (data) => data.phase = 'finale',
    },
    {
      id: 'UWU Garuda Woken',
      type: 'GainsEffect',
      netRegex: { target: 'Garuda', effectId: '5F9', capture: false },
      sound: 'Long',
      run: (data) => data.garudaAwoken = true,
    },
    {
      id: 'UWU Ifrit Woken',
      type: 'GainsEffect',
      netRegex: { target: 'Ifrit', effectId: '5F9', capture: false },
      sound: 'Long',
      run: (data) => data.ifritAwoken = true,
    },
    {
      id: 'UWU Titan Woken',
      type: 'GainsEffect',
      netRegex: { target: 'Titan', effectId: '5F9', capture: false },
      sound: 'Long',
    },
    {
      id: 'UWU Thermal Low Gain',
      type: 'GainsEffect',
      netRegex: { effectId: '5F5' },
      run: (data, matches) => data.thermalLow[matches.target] = parseInt(matches.count),
    },
    {
      id: 'UWU Thermal Low Lose',
      type: 'LosesEffect',
      netRegex: { effectId: '5F5' },
      run: (data, matches) => data.thermalLow[matches.target] = 0,
    },
    {
      id: 'UWU Beyond Limits Gain',
      type: 'GainsEffect',
      netRegex: { effectId: '5FA' },
      run: (data, matches) => data.beyondLimits.add(matches.target),
    },
    {
      id: 'UWU Beyond Limits Lose',
      type: 'LosesEffect',
      netRegex: { effectId: '5FA' },
      run: (data, matches) => data.beyondLimits.delete(matches.target),
    },
    // --------- Garuda ----------
    {
      id: 'UWU Garuda Slipstream',
      type: 'StartsUsing',
      netRegex: { id: '2B53', source: 'Garuda', capture: false },
      response: Responses.getBehind(),
      run: (data) => data.slipstreamCount++,
    },
    {
      id: 'UWU Garuda Downburst',
      // This always comes after a Slipstream so use that to trigger.
      // There is no castbar and the ability ids are the same.
      type: 'Ability',
      netRegex: { id: '2B53', source: 'Garuda', capture: false },
      delaySeconds: (data) => data.slipstreamCount === 4 ? 10 : 0,
      suppressSeconds: 3,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          // TODO: we could track who Garuda is tanking here and say "on you" or "stack on"
          tankCleave: Outputs.tankCleave,
          partyStack: Outputs.stackMarker,
          tankCleavePartyOut: {
            en: 'Tank Cleave (PARTY OUT)',
            de: 'Tank Cleave (GRUPPE RAUS)',
            fr: 'Tank cleave (Groupe à l\'extérieur)',
            ja: 'タンク頭割り (PTは外へ)',
            cn: '坦克顺劈 (人群避开)',
            ko: '광역 탱버 (본대 밖으로)',
            tc: '坦克順劈 (人群避開)',
          },
        };

        if (data.slipstreamCount === 1 || data.slipstreamCount > 4)
          return;

        // You need to have awoken Garuda by this point to beat the fight, but if you haven't
        // and are just progging it's easy to forget that this is not a party share.
        // This is also mostly skipped these days.
        if (!data.garudaAwoken && data.slipstreamCount === 4)
          return { alarmText: output.tankCleavePartyOut!() };

        if (data.garudaAwoken)
          return { alertText: output.partyStack!() };
        return { infoText: output.tankCleave!() };
      },
    },
    {
      id: 'UWU Garuda Mistral Song Marker',
      type: 'HeadMarker',
      netRegex: { id: '0010' },
      condition: Conditions.targetIsYou(),
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Mistral on YOU',
          de: 'Mistral-Song',
          fr: 'Mistral sur VOUS',
          ja: 'ミストラルソング',
          cn: '寒风之歌点名',
          ko: '삭풍 징',
          tc: '寒風之歌點名',
        },
      },
    },
    {
      id: 'UWU Garuda Mistral Song Tank',
      type: 'HeadMarker',
      netRegex: { id: '0010', capture: false },
      condition: (data) => data.role === 'tank',
      suppressSeconds: 5,
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Block Mistral Song',
          de: 'Mistral-Song',
          fr: 'Chant du mistral',
          ja: 'ミストラルソング',
          cn: '寒风之歌',
          ko: '삭풍 징',
          tc: '寒風之歌',
        },
      },
    },
    {
      id: 'UWU Garuda Spiny Plume',
      type: 'AddedCombatant',
      netRegex: { name: 'Spiny Plume', capture: false },
      condition: (data) => data.role === 'tank',
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Spiny Plume Add',
          de: 'Dorniger Federsturm',
          fr: 'Add Plume perforante',
          ja: 'スパイニープルーム',
          cn: '刺羽出现',
          ko: '가시돋힌 깃털 등장',
          tc: '刺羽出現',
        },
      },
    },
    {
      id: 'UWU Garuda Wicked Wheel',
      type: 'StartsUsing',
      netRegex: { id: '2B4E', source: 'Garuda', capture: false },
      condition: (data) => data.phase === 'garuda',
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          unawokenOut: Outputs.out,
          awokenOutThenIn: Outputs.outThenIn,
        };
        if (data.garudaAwoken)
          return { alertText: output.awokenOutThenIn!() };
        return { infoText: output.unawokenOut!() };
      },
    },
    {
      id: 'UWU Garuda Aerial Blast',
      type: 'StartsUsing',
      netRegex: { id: '2B55', source: 'Garuda', capture: false },
      condition: (data) => data.phase === 'garuda',
      response: Responses.aoe(),
    },
    {
      id: 'UWU Garuda Sisters Location',
      comment: {
        en:
          'Where the two sisters are for the tanks to block. dir1 is always the first sister location starting North and going clockwise',
        de:
          'Wo sich die beiden Schwestern befinden, die die Tanks blockieren sollen. dir1 ist immer die erste Schwester, die im Norden beginnt und im Uhrzeigersinn verläuft.',
        fr:
          'L\'emplacement des deux sœurs à bloquer pour les tanks. dir1 est toujours le premier emplacement de la sœur en commençant par le nord et en allant dans le sens des aiguilles d\'une montre.',
        ja: 'タンクがブロックする2人の分身の位置。dir1 は基本的に「北」から始まり、時計回りに最初の分身の位置に戻ります。',
        cn: '两分身待坦克阻挡的位置。dir1 始终是从地图上方开始顺时针方向的第一个分身位置',
        ko: '탱커가 막을 두 분신의 위치. dir1은 북쪽에서 시계방향으로 도는 것을 기준으로 항상 첫 번째 분신의 위치입니다',
        tc: '兩分身待坦克阻擋的位置。dir1 始終是從地圖上方開始順時針方向的第一個分身位置',
      },
      type: 'StartsUsing',
      netRegex: { id: '2B55', source: 'Garuda', capture: false },
      /*
      [21:13:53.626] StartsCasting 14:400188D4:Garuda:2B55:Aerial Blast:400188D4:Garuda:2.700:100.00:100.00:0.00:0.00
      [21:13:56.607] AOEActionEffect 16:400188D4:Garuda:2B55:Aerial Blast:XXXXXXXX:Tiny Poutini:350003:1F560000:1B:2B558000:0:0:0:0:0:0:0:0:0:0:0:0:22867:25795:10000:10000:::100.33:97.62:0.00:0.01:693098:1664845:34464:10000:::100.00:100.00:0.00:0.00:0000D57F:7:8
      [21:14:07.587] 261 105:Change:400188CC:Heading:0.0000:PosX:94.0000:PosY:100.0000:PosZ:0.0000
      [21:14:07.587] 261 105:Change:400188CB:Heading:0.0000:PosX:106.0000:PosY:100.0000:PosZ:0.0000
      [21:14:11.772] StartsCasting 14:400188C6:Garuda:2B4D:Feather Rain:E0000000::0.700:101.06:101.93:0.00:0.00
      [21:14:11.772] StartsCasting 14:400188C7:Garuda:2B4D:Feather Rain:E0000000::0.700:101.30:101.74:0.00:0.00
      [21:14:11.772] StartsCasting 14:400188C8:Garuda:2B4D:Feather Rain:E0000000::0.700:100.79:102.21:0.00:0.00
      [21:14:11.772] StartsCasting 14:400188C9:Garuda:2B4D:Feather Rain:E0000000::0.700:100.50:101.45:0.00:0.00
      [21:14:11.772] StartsCasting 14:400188CA:Garuda:2B4D:Feather Rain:E0000000::0.700:100.80:102.30:0.00:0.00
      [21:14:11.969] 261 105:Change:400188CB:Heading:0.0000:ModelStatus:16384:PosX:100.0000:PosY:80.5000:PosZ:0.0000
      [21:14:11.969] 261 105:Change:400188CC:Heading:3.1416:ModelStatus:16384:PosX:100.0000:PosY:119.5000:PosZ:0.0000
      [21:14:14.448] TargetIcon 1B:XXXXXXXX:Tiny Poutini:0000:0000:0010:0000:0000:0000
      */
      condition: (data) => data.phase === 'garuda',
      delaySeconds: 19,
      promise: async (data) => {
        data.combatantData = [];

        // TODO: it'd be nice if this function allowed filtering by name ids.
        data.combatantData = (await callOverlayHandler({
          call: 'getCombatants',
        })).combatants;
      },
      alertText: (data, _matches, output) => {
        // These two sisters are added before the pull starts,
        // but they are the only two with these names.
        // 1645 = Suparna
        // 1646 = Chirada
        const sisters = data.combatantData.filter((x) =>
          x.BNpcNameID === 1645 || x.BNpcNameID === 1646
        );
        const [dir1, dir2] = sisters.map((c) =>
          Directions.xyTo4DirNum(c.PosX, c.PosY, centerX, centerY)
        ).sort();
        if (dir1 === undefined || dir2 === undefined || sisters.length !== 2)
          return;

        const map: { [dir: number]: string } = {
          0: output.dirN!(),
          1: output.dirE!(),
          2: output.dirS!(),
          3: output.dirW!(),
        } as const;
        return output.text!({ dir1: map[dir1], dir2: map[dir2] });
      },
      outputStrings: {
        text: {
          en: 'Sisters: ${dir1} / ${dir2}',
          de: 'Schwestern: ${dir1} / ${dir2}',
          fr: 'Sœurs : ${dir1} / ${dir2}',
          ja: '分身: ${dir1} / ${dir2}',
          cn: '分身：${dir1} / ${dir2}',
          ko: '분신: ${dir1} / ${dir2}',
          tc: '分身：${dir1} / ${dir2}',
        },
        // TODO: the lint fails if you `...Directions.outputStringsCardinalDir` :C
        dirN: Outputs.dirN,
        dirE: Outputs.dirE,
        dirS: Outputs.dirS,
        dirW: Outputs.dirW,
      },
    },
    {
      id: 'UWU Ultima Mesohigh Tether',
      type: 'Tether',
      // This happens in Garuda, as well in Annihilation and Suppression.
      netRegex: { id: '0004', capture: false },
      suppressSeconds: 30,
      response: (data, _matches, output) => {
        // cactbot-builtin-response
        output.responseOutputStrings = {
          // The person with two stacks must get a tether.
          garuda2: {
            en: 'Get Sister Tether!!!',
            de: 'Nimm Verbindung von der Schwester!!!',
            fr: 'Prenez le lien de la sœur !!!',
            ja: '分身の線を取って!!!',
            cn: '接分身的线!!!',
            ko: '분신 줄 가져가기!!!',
            tc: '接分身的線!!!',
          },
          // Other people with 1 stack can be informed about it.
          garuda1: {
            en: 'Sister Tethers',
            de: 'Schwester Verbindungen',
            fr: 'Lien de la sœur',
            ja: '分身の線',
            cn: '分身连线',
            ko: '분신 줄',
            tc: '分身連線',
          },
          // Usually static on a ranged.
          annihilation1: {
            en: 'Tether',
            de: 'Verbindungen',
            fr: 'Lien',
            ja: '線',
            cn: '连线',
            ko: '줄',
            tc: '連線',
          },
          // Late in the raid, so make sure anybody with a stack remembers this.
          suppression1: {
            en: 'Tether!!!',
            de: 'Verbindungen!!!',
            fr: 'Lien !!!',
            ja: '線!!!',
            cn: '连线!!!',
            ko: '줄!!!',
            tc: '連線!!!',
          },
        };

        const myStacks = data.thermalLow[data.me];
        if (myStacks === undefined || myStacks === 0)
          return;

        if (myStacks === 2) {
          if (data.phase === 'garuda' && !data.garudaAwoken)
            return { alarmText: output.garuda2!() };
          return;
        }

        if (data.phase === 'garuda')
          return { alertText: output.garuda1!() };

        if (data.phase === 'annihilation')
          return { infoText: output.annihilation1!() };

        if (data.phase === 'suppression')
          return { alarmText: output.suppression1!() };
      },
    },
    // --------- Ifrit ----------
    {
      id: 'UWU Ifrit Possible ID Locator',
      type: 'StartsUsing',
      netRegex: { id: '2B55', source: 'Garuda', capture: false },
      // Run this after the initial Garuda trigger and just piggyback off its call to `getCombatants`
      // We're just looking to pluck the four possible IDs from the array pre-emptively to avoid doing
      // that filter on every `SetActorPos` line
      delaySeconds: 25,
      run: (data) => {
        data.possibleIfritIDs = data.combatantData
          .filter((c) => c.BNpcNameID === 0x4A1)
          .map((c) => c.ID?.toString(16).toUpperCase() ?? '');
      },
    },
    {
      id: 'UWU Ifrit Initial Dash Collector',
      type: 'ActorSetPos',
      // Filter to only enemy actors for performance
      netRegex: { id: '4[0-9A-Fa-f]{7}', capture: true },
      condition: (data, matches) => {
        if (!data.possibleIfritIDs.includes(matches.id))
          return false;
        const posXVal = parseFloat(matches.x ?? '0');
        const posYVal = parseFloat(matches.y ?? '0');

        if (posXVal === 0 || posYVal === 0)
          return false;

        // If the Ifrit actor has jumped to exactly 19.5 out on a cardinal, that's our dash spot
        if (
          Math.abs(posXVal - 100) - 19.5 < Number.EPSILON ||
          Math.abs(posYVal - 100) - 19.5 < Number.EPSILON
        )
          return true;

        return false;
      },
      suppressSeconds: 9999,
      infoText: (data, matches, output) => {
        const posXVal = parseFloat(matches.x ?? '0');
        const posYVal = parseFloat(matches.y ?? '0');

        let ifritDir: DirectionOutputCardinal = 'unknown';

        // Flag both sides that ifrit is dashing through as unsafe, while also tracking where he's actually
        // jumped to so we can use it for the infoText
        if (posXVal < 95) {
          data.ifritRadiantPlumeLocations.push('dirW', 'dirE');
          ifritDir = 'dirW';
        } else if (posXVal > 105) {
          data.ifritRadiantPlumeLocations.push('dirW', 'dirE');
          ifritDir = 'dirE';
        } else if (posYVal < 95) {
          data.ifritRadiantPlumeLocations.push('dirN', 'dirS');
          ifritDir = 'dirN';
        } else if (posYVal > 105) {
          data.ifritRadiantPlumeLocations.push('dirN', 'dirS');
          ifritDir = 'dirS';
        }

        // Remove duplicates
        data.ifritRadiantPlumeLocations = data.ifritRadiantPlumeLocations
          .filter((pos, index) => data.ifritRadiantPlumeLocations.indexOf(pos) === index);

        return output.text!({ dir: output[ifritDir]!() });
      },
      outputStrings: {
        text: {
          en: 'Ifrit ${dir}',
          de: 'Ifrit ${dir}',
          fr: 'Ifrit ${dir}',
          ja: 'イフリート ${dir}',
          cn: '火神 ${dir}',
          ko: '이프리트 ${dir}',
          tc: '火神 ${dir}',
        },
        unknown: Outputs.unknown,
        ...Directions.outputStringsCardinalDir,
      },
    },
    {
      id: 'UWU Ifrit Initial Radiant Plume Collector',
      type: 'StartsUsingExtra',
      netRegex: { id: '2B61' },
      condition: (data, matches) => {
        const posXVal = parseFloat(matches.x);
        const posYVal = parseFloat(matches.y);

        // Possible plume locations:
        // 100.009, 106.998
        // 100.009, 118.015  = south
        // 100.009, 92.990
        // 100.009, 82.003   = north
        // 106.998, 100.009
        // 110.996, 110.996
        // 110.996, 88.992
        // 118.015, 100.009  = east
        // 82.003,  100.009  = west
        // 88.992,  110.996
        // 88.992,  88.992
        // 92.990,  100.009

        if (Math.abs(posXVal - 100) < 1) {
          if (Math.abs(posYVal - 83) < 1) {
            // North unsafe
            data.ifritRadiantPlumeLocations.push('dirN');
          } else if (Math.abs(posYVal - 118) < 1) {
            // South unsafe
            data.ifritRadiantPlumeLocations.push('dirS');
          }
        } else if (Math.abs(posYVal - 100) < 1) {
          if (Math.abs(posXVal - 83) < 1) {
            // West unsafe
            data.ifritRadiantPlumeLocations.push('dirW');
          } else if (Math.abs(posXVal - 118) < 1) {
            // East unsafe
            data.ifritRadiantPlumeLocations.push('dirE');
          }
        }

        // Remove duplicates
        data.ifritRadiantPlumeLocations = data.ifritRadiantPlumeLocations
          .filter((pos, index) => data.ifritRadiantPlumeLocations.indexOf(pos) === index);

        // 3 danger spots means we only have one safe spot left
        return data.ifritRadiantPlumeLocations.length === 3;
      },
      suppressSeconds: 5,
      infoText: (data, _matches, output) => {
        if (data.ifritRadiantPlumeLocations.length < 3)
          return;

        const safeDir =
          Directions.outputCardinalDir.filter((dir) =>
            !data.ifritRadiantPlumeLocations.includes(dir)
          )[0];

        return output[safeDir ?? 'unknown']!();
      },
      outputStrings: {
        unknown: Outputs.unknown,
        ...Directions.outputStringsCardinalDir,
      },
    },
    {
      id: 'UWU Ifrit Vulcan Burst',
      type: 'StartsUsing',
      netRegex: { id: '25B7', source: 'Ifrit', capture: false },
      response: Responses.knockback(),
    },
    {
      id: 'UWU Ifrit Nail Adds',
      type: 'AddedCombatant',
      netRegex: { npcNameId: '1186', npcBaseId: '8731' },
      condition: (data, matches) => {
        data.nailAdds.push(matches);
        return data.nailAdds.length === 4;
      },
      alertText: (data, _matches, output) => {
        // Nails are always on cardinals and intercardinals.
        // The back two nails are always 135 degrees apart and the front two are 45 degrees apart,
        // and the front and back nails are 90 degrees apart from each other. Thus, you can figure
        // out the orientation based on relative positions from the origin.
        //
        // One possible example of directions:
        // 0 = back right
        // 3 = back left
        // 5 = front left
        // 6 = front right
        const dirs = data.nailAdds.map((m) => {
          return Directions.addedCombatantPosTo8Dir(m, centerX, centerY);
        }).sort();
        for (let i = 0; i < dirs.length; ++i) {
          const this8Dir = dirs[i];
          const next8Dir = dirs[(i + 1) % dirs.length];
          if (this8Dir === undefined || next8Dir === undefined)
            break;
          // The two close nails are 45 degrees apart.
          if (next8Dir - this8Dir === 1 || this8Dir - next8Dir === 7) {
            const between16Dir = this8Dir * 2 + 1;
            const outputKey = Directions.output16Dir[between16Dir] ?? 'unknown';
            return output.text!({ dir: output[outputKey]!() });
          }
        }
      },
      outputStrings: {
        text: {
          en: 'Near: ${dir}',
          de: 'Nahe: ${dir}',
          fr: 'Proche : ${dir}',
          ja: '近いほう: ${dir}',
          cn: '近: ${dir}',
          ko: '가까운 기둥: ${dir}',
          tc: '近: ${dir}',
        },
        ...Directions.outputStrings16Dir,
      },
    },
    {
      id: 'UWU Ifrit Nail Deaths',
      type: 'Ability',
      netRegex: { id: '2B58' },
      condition: (data, matches) => {
        if (data.nailDeaths[matches.sourceId] === undefined) {
          data.nailDeaths[matches.sourceId] = matches;
          data.nailDeathOrder.push(matches.sourceId);
        }
        return data.nailDeathOrder.length === 4;
      },
      suppressSeconds: 999999,
      run: (data) => {
        // No need to check awoken status here, we'll just look for the status effect later.
        const idToDir: { [id: string]: number } = {};

        // lastDir is an 8-value direction but modulo 4.
        let lastDir: number | undefined;
        let lastRotationDir: 'cw' | 'ccw' | undefined;
        for (const key of data.nailDeathOrder) {
          const m = data.nailDeaths[key];
          if (m === undefined)
            return;
          const x = parseFloat(m.x);
          const y = parseFloat(m.y);
          // Since dashes go through one direction and its opposite, map to N/NE/E/SE zone.
          // Consider a valid kill order to be sequential in either direction.
          // Most people do Z or reverse Z, but there's many valid orders (e.g. bowtie/fish).
          const this8Dir = Directions.xyTo8DirNum(x, y, centerX, centerY);
          idToDir[m.sourceId] = this8Dir;
          const thisDir = this8Dir % 4;
          if (lastDir === undefined) {
            lastDir = thisDir;
            continue;
          }

          const isCW = thisDir - lastDir === 1 || lastDir - thisDir === 3;
          const isCCW = lastDir - thisDir === 1 || thisDir - lastDir === 3;
          const thisRotationDir = isCW ? 'cw' : isCCW ? 'ccw' : undefined;
          lastDir = thisDir;

          // Invalid nail kill order.
          if (thisRotationDir === undefined)
            return;
          if (lastRotationDir === undefined) {
            lastRotationDir = thisRotationDir;
            continue;
          }
          // Invalid nail kill order.
          if (thisRotationDir !== lastRotationDir)
            return;
        }

        const firstNailId = data.nailDeathOrder[0];
        const lastNailId = data.nailDeathOrder[3];

        data.nailDeathRotationDir = lastRotationDir;
        if (firstNailId !== undefined)
          data.nailDeathFirst8Dir = idToDir[firstNailId];
        if (lastNailId !== undefined)
          data.nailDeathLast8Dir = idToDir[lastNailId];
      },
    },
    {
      id: 'UWU Ifrit Fetters',
      type: 'Tether',
      // This is GainsEffect effectId 179 as well applied to each player
      // but reapplied when the count changes due to distance.
      netRegex: { id: '0009' },
      condition: (data, matches) => matches.target === data.me || matches.source === data.me,
      infoText: (data, matches, output) => {
        const otherPlayer = matches.target === data.me ? matches.source : matches.target;
        return output.fetters!({ player: data.party.member(otherPlayer) });
      },
      outputStrings: {
        fetters: {
          en: 'Fetters (w/${player})',
          de: 'Fesseln (mit ${player})',
          fr: 'Entraves (avec ${player})',
          ja: '鎖 (相手: ${player})',
          cn: '锁链 (与 /${player})',
          ko: '사슬 (+${player})',
          tc: '鎖鏈 (與 ${player})',
        },
      },
    },
    {
      id: 'UWU Ifrit Searing Wind',
      type: 'StartsUsing',
      netRegex: { id: '2B5B', source: 'Ifrit' },
      condition: Conditions.targetIsYou(),
      alarmText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Searing Wind on YOU',
          de: 'Versengen auf DIR',
          fr: 'Carbonisation sur VOUS',
          ja: '自分に灼熱',
          cn: '灼热咆哮点名',
          ko: '작열 대상자',
          tc: '灼熱咆哮點名',
        },
      },
    },
    {
      id: 'UWU Ifrit Hellfire',
      type: 'StartsUsing',
      netRegex: { id: '2B5E', source: 'Ifrit', capture: false },
      condition: (data) => data.phase === 'ifrit',
      response: Responses.aoe(),
    },
    {
      id: 'UWU Ifrit Name Toggle Counter',
      type: 'NameToggle',
      netRegex: { name: 'Ifrit', toggle: '00', capture: false },
      run: (data) => data.ifritUntargetableCount++,
    },
    {
      id: 'UWU Ifrit Dash Safe Spot 1',
      // TODO: we could add a config option for the other set of safe spots, as it's also
      // valid to go SW/NE instead of SE/NW for the first dashes. It would just change
      // the adjust call.
      comment: {
        en: `If the first nail is SE, this will call SE/NW for both reverse-Z and normal-Z.
             If the first nail is S, this will call SE/NW for reverse-Z and SW/NE for normal-Z.
             Other nail orders are also supported, these are just examples.`,
        de:
          `Wenn der erste Nagel SO ist, wird dies SO/NW sowohl für Umgekehrtes-Z als auch für Normal-Z aufgerufen.
             Wenn der erste Nagel S ist, wird dies SO/NW für Umgekehrtes-Z und SW/NO für Normal-Z aufgerufen.
             Andere Nagelreihenfolgen werden ebenfalls unterstützt, dies sind nur Beispiele.`,
        fr: `Si le premier clou est SE, on annoncera SE/NO pour les Z inversés et les Z normaux.
             Si le premier clou est S, on annoncera SE/NO pour la zone inversée et SW/NO pour la zone normale.
             D'autres ordres de clous sont également possibles, il ne s'agit que d'exemples.`,
        ja: `最初の楔が南東の場合、逆Zと通常Zの両方で南東/北西に出現します。
             最初の楔が南の場合、逆Zなら南東/北西、通常Zなら南西/北東に出現します。
             これは一例で、他の釘の順序もサポートされています。`,
        cn: `如果第一个火神柱在右下，则反向 Z 和正常 Z 都会提示右下/左上
             如果第一个火神柱在下, 则反向 Z 将提示右下/左上，正常 Z 将提示左下/右上。
             这些只是举例, 其他火神柱顺序也支持。`,
        ko: `첫 번째 기둥이 남동쪽인 경우, 역방향 Z와 일반 Z 모두에 대해 남동/북서를 호출합니다.
             첫 번째 기둥이 남쪽인 경우, 역방향 Z는 남동/북서를, 일반 Z는 남서/북동를 호출합니다.
             다른 기둥 순서도 지원되며, 이는 예시일 뿐입니다.`,
        tc: `如果第一個火神柱在東南，則反向 Z 和正常 Z 都會提示東南/西北
             如果第一個火神柱在南面, 則反向 Z 將提示東南/西北，正常 Z 將提示西南/東北。
             這些只是舉例, 其他火神柱順序也支援。`,
      },
      type: 'NameToggle',
      netRegex: { name: 'Ifrit', toggle: '00', capture: false },
      condition: (data) => data.ifritUntargetableCount === 1,
      durationSeconds: 5,
      alertText: (data, _matches, output) => {
        const firstNailDir = data.nailDeathFirst8Dir;
        const rotationType = data.nailDeathRotationDir;
        if (firstNailDir === undefined || rotationType === undefined)
          return;

        const oppositeRotation = rotationType === 'cw' ? 7 : 1;
        const isIntercard = firstNailDir % 2 === 1;

        // For the first jump, we need to end on an intercard.
        // If we're already on an intercard, just stop there. IMO, it's better to have the first
        // Ifrit jump directly on the party where it's obvious which way you need to adjusut.
        // For the second jump, the first Ifrit jumps directly on the first nail location,
        // if that's not an intercard, initial start is good, otherwise party needs to rotate 45
        // in the opposite direction they will be rotating to avoid dashes.
        const dir1 = isIntercard ? firstNailDir : (firstNailDir + oppositeRotation) % 8;
        const dir2 = (dir1 + 4) % 8;
        const dir1Str = output[Directions.outputFrom8DirNum(dir1)]!();
        const dir2Str = output[Directions.outputFrom8DirNum(dir2)]!();
        return output.intercardSafeSpot!({ dir1: dir1Str, dir2: dir2Str });
      },
      outputStrings: {
        intercardSafeSpot: {
          en: '${dir1} / ${dir2}',
          de: '${dir1} / ${dir2}',
          fr: '${dir1} / ${dir2}',
          ja: '${dir1} / ${dir2}',
          cn: '${dir1} / ${dir2}',
          ko: '${dir1} / ${dir2}',
          tc: '${dir1} / ${dir2}',
        },
        ...Directions.outputStrings8Dir,
      },
    },
    {
      id: 'UWU Ifrit Dash Safe Spot 2 Adjust',
      comment: {
        en: `If the first nail was on an intercard, then the first Ifrit dash is on an intercard
             and this optional call is to move to be adjacent to that first dash.
             If you are already safe, this will not be called.`,
        de:
          `Wenn der erste Nagel Interkardinal war, dann ist der erste Ifrit-Ansturm auf einer Interkardinalen
             und dieser optionale Aufruf besteht darin, sich in die Nähe dieses ersten Ansturms zu bewegen.
             Wenn man bereits in Sicherheit ist, wird dies nicht aufgerufen.`,
        fr:
          `Si le premier clou était en intercardinal, alors le premier dash d'Ifrit est en intercardinal
             et cette annonce optionnelle vous préviens de vous déplacer pour être adjacent à ce premier dash.
             Si vous êtes déjà en sécurité, cette option n'est pas activée.`,
        ja: `最初の楔が北東、南東、南西、北西なら、最初のイフリートの突進も北東、南東、南西、北西になり、
             このオプションはその最初のダッシュに隣接するように移動するためのものです。
             すでに安地にいる場合、これは呼び出されません。`,
        cn: `如果第一个火神柱在对角线上，那么第一次火神冲也在对角线上。
             这个可选提示会提示你移动到第一次火神冲附近的位置。
             如果你已在安全区，则不会输出此提示。`,
        ko: `첫 번째 기둥이 대각선에 있으면 첫 번째 이프리트 돌진도 대각선에 있으며,
             이 알람은 첫 번째 돌진 옆으로 이동하라는 것이 됩니다.
             이미 안전하다면 이 알람은 호출되지 않습니다.`,
        tc: `如果第一個火神柱在對角線上，那麼第一次火神衝也在對角線上。
             這個可選提示會提示你移動到第一次火神衝附近的位置。
             如果你已在安全區，則不會輸出此提示。`,
      },
      type: 'NameToggle',
      netRegex: { name: 'Ifrit', toggle: '00', capture: false },
      // Unfortunately no way to know for sure if Ifrit dies before dashes.
      condition: (data) => data.ifritUntargetableCount === 2 && data.ifritAwoken,
      infoText: (data, _matches, output) => {
        const firstNailDir = data.nailDeathFirst8Dir;
        const rotationType = data.nailDeathRotationDir;
        if (firstNailDir === undefined || rotationType === undefined)
          return;

        // If we didn't start on an intercard, then we are already safe.
        const isIntercard = firstNailDir % 2 === 1;
        if (!isIntercard)
          return;

        // Adjust the opposite direction of rotation, e.g. we are rotating clockwise
        // and Ifrit hops on the party SE, the party needs to go to E to rotate into
        // the first Ifrit at SE.
        const dirStr = rotationType === 'cw' ? output.counterclockwise!() : output.clockwise!();
        return output.text!({ rotation: dirStr });
      },
      outputStrings: {
        text: {
          en: 'Adjust 45° ${rotation}',
          de: 'Rotiere 45° ${rotation}',
          fr: 'Ajustez de 45° ${rotation}',
          ja: '45° ${rotation} に調整',
          cn: '${rotation} 旋转 45°',
          ko: '${rotation} 45° 이동',
          tc: '${rotation} 旋轉 45°',
        },
        clockwise: Outputs.clockwise,
        counterclockwise: Outputs.counterclockwise,
      },
    },
    {
      id: 'UWU Ifrit Dash Safe Spot 2',
      comment: {
        en: `This is the major movement for the Ifrit dashes starting adjacent to the first dash.
             Both the party and the healer will move either 45 or 90 degrees.
             It is a "fast" movement if you need to move fast to avoid the Ifrit follow-up dash.
             It is a "slow" movement if you have extra time to do this.`,
        de:
          `Dies ist die Hauptbewegung für die Ifrit-Anstürme, die in der Nähe des ersten Ansturms beginnt.
             Sowohl die Gruppe als auch der Heiler bewegen sich entweder um 45 oder 90 Grad.
             Es ist eine "schnelle" Bewegung, wenn man sich schnell bewegen muss, um dem Ifrit-Folgeschlag auszuweichen.
             Es ist eine "langsame" Bewegung, wenn man mehr Zeit hat, dies zu tun.`,
        fr:
          `Il s'agit du mouvement principal pour les dashs d'Ifrit qui commencent à côté du premier dash.
             Le groupe et le soigneur se déplacent de 45 ou 90 degrés.
             Il s'agit d'un mouvement "rapide" si vous devez vous déplacer rapidement pour éviter le dash suivant d'Ifrit.
             Il s'agit d'un mouvement "lent" si vous avez plus de temps pour le faire.`,
        ja: `これは、最初の突進から連続するイフリート突進の移動方法です。
             パーティ全体とヒーラーは、45度か90度を判断して移動します。
             連続突進を避けるために、すぐ移動する必要がある場合は「急」です。
             余裕がある場合は「遅」となります。`,
        cn: `这是从第一次火神冲附近开始的火神冲主要移动。
             人群和奶妈都将移动 45 度或 90 度。
             "快" 表示需要快速移动才能躲开火神冲。
             "慢" 表示移动时间相对比较充足。`,
        ko: `첫 번째 돌진 직후부터 시작되는 이프리트 돌진의 주요 동선입니다.
             본대와 힐러 모두 45도 또는 90도로 움직입니다.
             이프리트의 후속 돌진을 피하기 위해 빠르게 이동해야 하는 경우 "빠른" 이동입니다.
             시간적 여유가 있다면 "느린" 이동입니다.`,
        tc: `這是從第一次火神衝附近開始的火神衝主要移動。
             人群和奶媽都將移動 45 度或 90 度。
             "快" 表示需要快速移動才能躲開火神衝。
             "慢" 表示移動時間相對比較充足。`,
      },
      type: 'NameToggle',
      netRegex: { name: 'Ifrit', toggle: '00', capture: false },
      condition: (data) => data.ifritUntargetableCount === 2 && data.ifritAwoken,
      // Here's one log file example for this timing.
      // [20:38:36.510] NameToggle 22:40017C12:Ifrit:40017C12:Ifrit:00
      // [20:38:38.245] 261 105:Change:40017C12:Heading:2.3562:PosX:86.3000:PosY:113.7000:PosZ:0.0000
      // [20:38:40.919] StartsCasting 14:40017C0F:Ifrit:2B5F:Crimson Cyclone:40017C0F:Ifrit:2.700:113.70:113.70:0.00:-2.36
      // [20:38:42.343] StartsCasting 14:40017C11:Ifrit:2B5F:Crimson Cyclone:40017C11:Ifrit:2.700:100.00:80.50:0.00:0.00
      // [20:38:43.725] StartsCasting 14:40017C12:Ifrit:2B5F:Crimson Cyclone:40017C12:Ifrit:2.700:86.30:113.70:0.00:2.36
      // [20:38:45.152] StartsCasting 14:40017C10:Ifrit:2B5F:Crimson Cyclone:40017C10:Ifrit:2.700:80.50:100.00:0.00:1.57
      delaySeconds: 2.5,
      promise: async (data) => {
        data.combatantData = [];
        if (data.bossId.ifrit === undefined)
          return;

        // The real Ifrit is the one that is Awoken so find where he is.
        data.combatantData = (await callOverlayHandler({
          call: 'getCombatants',
          ids: [parseInt(data.bossId.ifrit, 16)],
        })).combatants;
      },
      alertText: (data, _matches, output) => {
        // If killed before dashes occur, and geocrush has started casting, suppress this.
        if (data.phase === 'titan')
          return;
        const [combatant] = data.combatantData;
        if (combatant === undefined || data.combatantData.length !== 1)
          return;

        const firstNailDir = data.nailDeathFirst8Dir;
        const rotationType = data.nailDeathRotationDir;
        if (firstNailDir === undefined || rotationType === undefined)
          return;

        const oppositeRotationDir = rotationType === 'cw' ? -1 : 1;
        const rotationDir = rotationType === 'cw' ? 1 : -1;
        const startDir = (firstNailDir + oppositeRotationDir + 8) % 8;
        const ifritDir = Directions.combatantStatePosTo8Dir(combatant, centerX, centerY);

        for (let i = 1; i <= 4; ++i) {
          const dashDir = (startDir + i * rotationDir + 8) % 8;
          if (dashDir % 4 !== ifritDir % 4)
            continue;

          // If Ifrit is the first or third dash, we rotate 45 degrees, otherwise 90.
          const finalRotation = (i === 1 || i === 3) ? rotationDir : rotationDir * 2;
          const finalDir = (startDir + finalRotation + 8) % 8;

          const finalDirStr = output[Directions.outputFrom8DirNum(finalDir)]!();
          const rotation = rotationType === 'cw' ? output.clockwise!() : output.counterclockwise!();
          if (i === 1)
            return output.awokenDash1!({ rotation: rotation, dir: finalDirStr });
          if (i === 2)
            return output.awokenDash2!({ rotation: rotation, dir: finalDirStr });
          if (i === 3)
            return output.awokenDash3!({ rotation: rotation, dir: finalDirStr });
          if (i === 4)
            return output.awokenDash4!({ rotation: rotation, dir: finalDirStr });
        }
      },
      outputStrings: {
        awokenDash1: {
          en: '${rotation} 45° to ${dir} (fast)',
          de: '${rotation} 45° nach ${dir} (schnell)',
          fr: '${rotation} 45° vers ${dir} (rapide)',
          ja: '${rotation} 45° ${dir} に (急)',
          cn: '${rotation} 45° 到 ${dir} (快)',
          ko: '${rotation} 45° ${dir}까지 (빠름)',
          tc: '${rotation} 45° 到 ${dir} (快)',
        },
        awokenDash2: {
          en: '${rotation} 90° to ${dir} (fast)',
          de: '${rotation} 90° nach ${dir} (schnell)',
          fr: '${rotation} 90° vers ${dir} (rapide)',
          ja: '${rotation} 90° ${dir} に (急)',
          cn: '${rotation} 90° 到 ${dir} (快)',
          ko: '${rotation} 90° ${dir}까지 (빠름)',
          tc: '${rotation} 90° 到 ${dir} (快)',
        },
        awokenDash3: {
          en: '${rotation} 45° to ${dir} (slow)',
          de: '${rotation} 45° nach ${dir} (langsam)',
          fr: '${rotation} 45° vers ${dir} (lent)',
          ja: '${rotation} 45° ${dir} に (遅)',
          cn: '${rotation} 45° 到 ${dir} (慢)',
          ko: '${rotation} 45° ${dir}까지 (느림)',
          tc: '${rotation} 45° 到 ${dir} (慢)',
        },
        awokenDash4: {
          en: '${rotation} 90° to ${dir} (slow)',
          de: '${rotation} 90° nach ${dir} (langsam)',
          fr: '${rotation} 90° vers ${dir} (lent)',
          ja: '${rotation} 90° ${dir} に (遅)',
          cn: '${rotation} 90° 到 ${dir} (慢)',
          ko: '${rotation} 90° ${dir}까지 (느림)',
          tc: '${rotation} 90° 到 ${dir} (慢)',
        },
        clockwise: Outputs.clockwise,
        counterclockwise: Outputs.counterclockwise,
        ...Directions.outputStrings8Dir,
      },
    },
    {
      id: 'UWU Ifrit Flaming Crush',
      type: 'HeadMarker',
      netRegex: { id: '0075', capture: false },
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Stack',
          de: 'Stack',
          fr: 'Packez-vous',
          ja: '頭割り',
          cn: '集合分摊',
          ko: '집합',
          tc: '集合分攤',
        },
      },
    },
    // --------- Titan ----------
    {
      id: 'UWU Titan Last Move Collector',
      type: 'ActorMove',
      netRegex: { id: '4[0-9a-fA-F]{7}', capture: true },
      condition: (data, matches) =>
        data.phase === 'titan' &&
        data.bossId.titan === matches.id,
      run: (data, matches) => {
        data.lastTitanMove = matches;
      },
    },
    {
      id: 'UWU Titan Jump Direction',
      type: 'NameToggle',
      netRegex: { id: '4[0-9a-fA-F]{7}', toggle: '00', capture: true },
      condition: (data, matches) =>
        data.phase === 'titan' &&
        data.bossId.titan === matches.id &&
        data.lastTitanMove !== undefined,
      // There is sometimes a "late" ActorMove packet that can arrive anywhere from ~45-180ms after
      // the NameToggle packet. A delay of 0.4s should be enough for safety without a belated call.
      delaySeconds: 0.4,
      alertText: (data, _matches, output) => {
        const unknownStr = output.safe!({ dir: output.unknown!() });
        const lastMove = data.lastTitanMove;

        // For the first jump, if the MT maintains precise north-facing positioning until the jump,
        // it's possible no ActorMove packet will be sent.
        // In that case, assume Titan is north-facing (south safe).
        if (lastMove === undefined)
          return data.seenTitanFirstJump ? unknownStr : output.safe!({ dir: output['dirS']!() });

        const titanX = parseFloat(lastMove.x);
        const titanY = parseFloat(lastMove.y);
        const titanHdg = parseFloat(lastMove.heading);

        // Titan's final heading will be facing one of the four cardinal jump sites.
        // We can identify the correct site by comparing Titan's heading to the heading of the
        // jump site (toward Titan) where the difference is +/-pi -- in other words, a
        // 180-degree angle deviation indicative of an identical ray.
        for (const site of jumpSites) {
          const hdgToTitan = getRelativeHdg(titanX, titanY, site.x, site.y);
          const ray = Math.abs(titanHdg - hdgToTitan);
          if (ray >= 3 && ray <= 3.28) // should be pi, but allow for rounding errors etc.
            return output.safe!({ dir: output[site.safe]!() });
        }
        return unknownStr;
      },
      run: (data) => data.seenTitanFirstJump = true,
      outputStrings: {
        safe: {
          en: 'Safe: ${dir}',
          de: 'Sicher: ${dir}',
          fr: 'Sur : ${dir}',
          ja: '安地: ${dir}',
          cn: '安全区: ${dir}',
          ko: '안전: ${dir}',
          tc: '安全區: ${dir}',
        },
        unknown: Outputs.unknown,
        ...Directions.outputStringsCardinalDir,
      },
    },
    {
      id: 'UWU Titan Bury Direction',
      type: 'AddedCombatant',
      netRegex: { npcNameId: '1803' },
      condition: (data, matches) => {
        (data.titanBury ??= []).push(matches);
        return data.titanBury.length === 5;
      },
      alertText: (data, _matches, output) => {
        const bombs = (data.titanBury ?? []).map((matches) => {
          return { x: parseFloat(matches.x), y: parseFloat(matches.y) };
        });
        if (bombs.length !== 5) {
          console.error(`Titan Bury: wrong bombs size: ${JSON.stringify(data.titanBury)}`);
          return;
        }
        // 5 bombs drop, and then a 6th later.
        // They all drop on one half of the arena, and then 3 on one half and 2 on the other.
        // e.g. all 5 drop on north half, 3 on west half, 2 on east half.
        const numDir = [0, 0, 0, 0]; // north, east, south, west
        for (const bomb of bombs) {
          if (bomb.y < centerY)
            numDir[0]++;
          else
            numDir[2]++;
          if (bomb.x < centerX)
            numDir[3]++;
          else
            numDir[1]++;
        }

        for (let idx = 0; idx < numDir.length; ++idx) {
          if (numDir[idx] !== 5)
            continue;
          // Example: dir is 1 (east), party is west, facing west.
          // We need to check dir 0 (north, aka "right") and dir 2 (south, aka "left").
          const numLeft = numDir[(idx + 1) % 4] ?? -1;
          const numRight = numDir[(idx - 1 + 4) % 4] ?? -1;

          if (numRight === 2 && numLeft === 3)
            return output.right!();
          if (numRight === 3 && numLeft === 2)
            return output.left!();

          console.error(
            `Titan Bury: bad counts: ${
              JSON.stringify(data.titanBury)
            }, ${idx}, ${numLeft}, ${numRight}`,
          );
          return;
        }

        console.error(`Titan Bury: failed to find dir: ${JSON.stringify(data.titanBury)}`);
      },
      outputStrings: {
        left: Outputs.left,
        right: Outputs.right,
      },
    },
    {
      id: 'UWU Titan Gaols',
      type: 'Ability',
      netRegex: { id: ['2B6C', '2B6B'], source: ['Garuda', 'Titan'] },
      condition: (data) => !data.seenTitanGaols,
      preRun: (data, matches) => {
        data.titanGaols.push(matches.target);
        if (data.titanGaols.length !== 3)
          return;

        const rawGaolOrder = [
          data.triggerSetConfig.gaolOrder1,
          data.triggerSetConfig.gaolOrder2,
          data.triggerSetConfig.gaolOrder3,
          data.triggerSetConfig.gaolOrder4,
          data.triggerSetConfig.gaolOrder5,
          data.triggerSetConfig.gaolOrder6,
          data.triggerSetConfig.gaolOrder7,
          data.triggerSetConfig.gaolOrder8,
          data.triggerSetConfig.gaolOrder9,
          data.triggerSetConfig.gaolOrder10,
          data.triggerSetConfig.gaolOrder11,
          data.triggerSetConfig.gaolOrder12,
          data.triggerSetConfig.gaolOrder13,
          data.triggerSetConfig.gaolOrder14,
          data.triggerSetConfig.gaolOrder15,
          data.triggerSetConfig.gaolOrder16,
          data.triggerSetConfig.gaolOrder17,
          data.triggerSetConfig.gaolOrder18,
          data.triggerSetConfig.gaolOrder19,
          data.triggerSetConfig.gaolOrder20,
        ].map((x) => x.trim()).filter((x) => x !== '');

        const partyNames = [...data.party.partyNames].sort((a, b) => a.localeCompare(b));

        // Merge jobs and names into a single list of names.
        const gaolOrder: string[] = [];
        for (const entry of rawGaolOrder) {
          if (entry.length !== 3) {
            gaolOrder.push(entry.toLocaleLowerCase());
            continue;
          }

          const uppercaseJobEntry = entry.toUpperCase();
          for (const name of partyNames) {
            const jobStr: string | undefined = data.party.jobName(name);
            if (jobStr === uppercaseJobEntry)
              gaolOrder.push(name.toLocaleLowerCase());
          }
        }

        data.titanGaols.sort((a, b) => {
          // Sort by `gaolOrder` and then alphabetical for names not in the list.
          const aIdx = gaolOrder.indexOf(a.toLocaleLowerCase());
          const bIdx = gaolOrder.indexOf(b.toLocaleLowerCase());
          if (aIdx === -1 && bIdx !== -1)
            return 1;
          if (bIdx === -1 && aIdx !== -1)
            return -1;
          if (aIdx < bIdx)
            return -1;
          if (bIdx < aIdx)
            return 1;
          return a.localeCompare(b);
        });

        if (data.options.Debug) {
          console.log(`GAOL CONFIG: ${JSON.stringify(rawGaolOrder)}`);
          console.log(`GAOL CONFIG NAME ORDER: ${JSON.stringify(gaolOrder)}`);
          console.log(`GAOL FINAL ORDER: ${JSON.stringify(data.titanGaols)}`);
        }
      },
      alertText: (data, _matches, output) => {
        if (data.titanGaols.length !== 3)
          return;
        const idx = data.titanGaols.indexOf(data.me);
        if (idx < 0)
          return;
        // Just return your number.
        return output[`num${idx + 1}`]!();
      },
      infoText: (data, _matches, output) => {
        if (data.titanGaols.length !== 3)
          return;
        return output.text!({
          player1: data.party.member(data.titanGaols[0]),
          player2: data.party.member(data.titanGaols[1]),
          player3: data.party.member(data.titanGaols[2]),
        });
      },
      outputStrings: {
        num1: Outputs.num1,
        num2: Outputs.num2,
        num3: Outputs.num3,
        text: {
          en: '${player1}, ${player2}, ${player3}',
          de: '${player1}, ${player2}, ${player3}',
          fr: '${player1}, ${player2}, ${player3}',
          ja: '${player1}, ${player2}, ${player3}',
          cn: '${player1}, ${player2}, ${player3}',
          ko: '${player1}, ${player2}, ${player3}',
          tc: '${player1}, ${player2}, ${player3}',
        },
      },
    },
    {
      // If anybody dies to bombs (WHY) and a rock is on them, then glhf.
      id: 'UWU Titan Bomb Failure',
      type: 'Ability',
      netRegex: { id: '2B6A', source: 'Bomb Boulder' },
      condition: (data) => !data.seenTitanGaols,
      alarmText: (data, matches, output) => {
        const idx = data.titanGaols.indexOf(matches.target);
        if (idx === -1)
          return;
        const numStr = output[`num${idx + 1}`]!();
        return output.text!({ num: numStr, player: data.party.member(matches.target) });
      },
      outputStrings: {
        // In case people want to replace 1/2/3 with front/mid/back or something.
        num1: Outputs.num1,
        num2: Outputs.num2,
        num3: Outputs.num3,
        text: {
          en: 'Everyone to ${num} (${player} died)',
          de: 'Alle zur ${num} (${player} ist gestorben)',
          fr: 'Tout le monde sur ${num} (${player} est mort)',
          ja: '${num} で (${player} が死亡)',
          cn: '所有人到 ${num} (${player}死亡)',
          ko: '전부다 ${num} 쪽으로 (${player} 죽음)',
          tc: '所有人到 ${num} (${player} 死亡)',
        },
      },
    },
    {
      id: 'UWU Titan Gaol Granite Impact',
      type: 'StartsUsing',
      netRegex: { id: '2B6D', capture: false },
      // Prevent Titan Bomb Failure from happening later in the phase.
      run: (data) => data.seenTitanGaols = true,
    },
    // --------- Intermission ----------
    {
      id: 'UWU Caster LB',
      type: 'AddedCombatant',
      // 2137 = Magitek Bit
      netRegex: { npcNameId: '2137', capture: false },
      condition: (data) =>
        Util.isCasterDpsJob(data.job) && data.beyondLimits.has(data.me) &&
        data.phase === 'intermission',
      suppressSeconds: 5,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Caster LB NOW!',
          de: 'Magier LB JETZT!',
          fr: 'LB MAINTENANT !',
          ja: 'キャスLB！',
          cn: '法系LB!',
          ko: '캐스터 리밋!',
          tc: '法系LB!',
        },
      },
    },
    {
      id: 'UWU Healer LB',
      type: 'Ability',
      // 2B73 = Blight
      netRegex: { id: '2B73', source: 'Lahabrea', capture: false },
      condition: (data) => Util.isHealerJob(data.job) && data.beyondLimits.has(data.me),
      suppressSeconds: 5,
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Healer LB NOW!',
          de: 'Heiler LB JETZT!',
          fr: 'Healer LB MAINTENANT !',
          ja: 'ヒラLB！',
          cn: '奶妈LB!',
          ko: '힐러 리밋!',
          tc: '補師LB!',
        },
      },
    },
    {
      id: 'UWU Melee LB',
      type: 'StartsUsing',
      // 2B74 = Dark IV
      netRegex: { id: '2B74', source: 'Lahabrea', capture: false },
      condition: (data) => Util.isMeleeDpsJob(data.job) && data.beyondLimits.has(data.me),
      alertText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Melee LB NOW!',
          de: 'Nahkämpfer LB JETZT!',
          fr: 'LB melee MAINTENANT !',
          ja: '近接LB！',
          cn: '近战LB!',
          ko: '근딜 리밋!',
          tc: '近戰LB!',
        },
      },
    },
    {
      id: 'UWU Ultima',
      type: 'StartsUsing',
      netRegex: { id: '2B8B', capture: false },
      condition: (data) => data.role === 'tank',
      alarmText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Tank LB NOW',
          de: 'JETZT Tank LB',
          fr: 'LB Tank MAINTENANT !',
          ja: '今タンクLB',
          cn: '坦克LB',
          ko: '탱리밋',
          tc: '坦克LB',
        },
      },
    },
    // --------- Predation ----------
    {
      id: 'UWU Predation',
      comment: {
        en: '"early safe" here means that you can move before the first Ifrit dash.',
        de:
          '"früh sicher" bedeutet hier, dass man such auch schon for dem ersten Ifrit Dash bewegen kann.',
        fr: '"sûr avant" veut dire que vous pouvez bouger avant le dash d\'Ifrit.',
        cn: '这里的 "提前安全" 指你可以在伊弗利特第一次冲锋前移动。',
        ko: '여기서 "안전"이란 첫 이프리트 돌진 전에 미리 가 있어도 된다는 의미입니다.',
        tc: '這裡的 "提前安全" 指你可以在伊弗利特第一次衝鋒前移動。',
      },
      type: 'StartsUsing',
      netRegex: { id: '2B76', source: 'The Ultima Weapon', capture: false },
      // [21:55:41.426] StartsCasting 14:4000BB88:The Ultima Weapon:2B76:Ultimate Predation:4000BB88:The Ultima Weapon:2.700:99.99:89.98:0.00:3.14
      // [21:55:44.404] ActionEffect 15:4000BB88:The Ultima Weapon:2B76:Ultimate Predation:4000BB88:The Ultima Weapon:0:0:0:0:0:0:0:0:0:0:0:0:0:0:0:0:3215688:3746181:0:10000:::99.99:89.98:0.00:3.14:3215688:3746181:0:10000:::99.99:89.98:0.00:3.14:0000A459:0:1
      // [21:55:48.850] NameToggle 22:4000BB88:The Ultima Weapon:4000BB88:The Ultima Weapon:00
      // [21:55:50.500] 261 105:Change:4000BB8E:Heading:-2.3563:PosX:103.0000:PosY:103.0000:PosZ:0.0000
      // [21:55:50.500] 261 105:Change:4000BB89:Heading:-1.5709:PosX:117.0000:PosY:97.0000:PosZ:0.0000
      // [21:55:50.500] 261 105:Change:4000BB8D:Heading:-2.3563:PosX:113.7000:PosY:113.7000:PosZ:0.0000
      // [21:55:50.500] 261 105:Change:4000BB88:Heading:2.3562:PosX:88.0000:PosY:112.0000:PosZ:0.0000
      // [21:55:56.229] StartsCasting 14:4000BB8D:Ifrit:2B5F:Crimson Cyclone:4000BB8D:Ifrit:2.700:113.70:113.70:0.00:-2.36
      delaySeconds: 10,
      durationSeconds: 5,
      promise: async (data) => {
        data.combatantData = [];

        const hexIds = Object.values(data.bossId);

        data.combatantData = (await callOverlayHandler({
          call: 'getCombatants',
          ids: hexIds.map((x) => parseInt(x, 16)),
        })).combatants;
      },
      alertText: (data, _matches, output) => {
        const findBoss = (key: BossKey): PluginCombatantState | undefined => {
          const hexId = data.bossId[key];
          if (hexId === undefined)
            return undefined;
          const decId = parseInt(hexId, 16);
          return data.combatantData.find((x) => x.ID === decId);
        };

        const garuda = findBoss('garuda');
        const ifrit = findBoss('ifrit');
        const titan = findBoss('titan');
        const ultima = findBoss('ultima');

        if (
          garuda === undefined || ifrit === undefined || titan === undefined || ultima === undefined
        )
          return;

        // Garuda always at +/- 3 from center on an intercardinal.
        const garudaDir = Directions.xyTo8DirNum(garuda.PosX, garuda.PosY, centerX, centerY);
        if (garudaDir % 2 === 0)
          return;

        // e.g. Garuda is NW (7), the two safe directions are E (2) and S (4).
        let safeDir = [(garudaDir + 3) % 8, (garudaDir + 5) % 8];

        // Titan appears slightly offset from a cardinal. Never run out towards Titan.
        // TODO: Titan is slightly offset and you could theoretically pick a slightly
        // safer cardinal in some cases (I think?) depending on how landslides aim from there.
        const titanDir = Directions.xyTo8DirNum(titan.PosX, titan.PosY, centerX, centerY);
        safeDir = safeDir.filter((x) => x !== titanDir);

        // Ultima appears on an intercardinal. If Ultima is adjacent to only one of the safe spots,
        // then pick the other safe spot because it will have more safe directions to run 2nd.
        const ultimaDir = Directions.xyTo8DirNum(ultima.PosX, ultima.PosY, centerX, centerY);

        const notAdjacentToUltima = safeDir.filter((x) => {
          const isAdjacentToUltima = x === (ultimaDir + 1) % 8 || ultimaDir === (x + 1) % 8;
          return !isAdjacentToUltima;
        });
        // If there's at least one cardinal not next to Ultima, pick one of those.
        if (notAdjacentToUltima.length !== 0)
          safeDir = safeDir.filter((x) => notAdjacentToUltima.includes(x));

        // Ifrit always is on an intercard and dashes through it.
        const ifritDir = Directions.xyTo8DirNum(ifrit.PosX, ifrit.PosY, centerX, centerY);

        const dirStrMap: { [dir: number]: string } = {
          0: output.dirN!(),
          2: output.dirE!(),
          4: output.dirS!(),
          6: output.dirW!(),
        } as const;

        // (1) Do any of our safe spots have an early safe spot where you could
        // go stand on the wall immediately?
        for (const dir of safeDir) {
          for (const run of [-1, 1]) {
            const final = (dir + run + 8) % 8;
            if (final === ultimaDir)
              continue;
            // Will Ifrit dash through this or the opposite side?
            if (final % 4 === ifritDir % 4)
              continue;
            const rotation = run === -1 ? output.counterclockwise!() : output.clockwise!();
            return output.early!({ dir: dirStrMap[dir], rotation: rotation });
          }
        }

        // (2) Are any safe spots opposite of Garuda (and not by Ultima)?
        const garudaOpposite = (garudaDir + 4) % 8;
        for (const dir of safeDir) {
          for (const run of [-1, 1]) {
            const final = (dir + run + 8) % 8;
            if (final === ultimaDir)
              continue;
            if (final !== garudaOpposite)
              continue;
            const rotation = run === -1 ? output.counterclockwise!() : output.clockwise!();
            return output.normal!({ dir: dirStrMap[dir], rotation: rotation });
          }
        }

        // (3) Otherwise, just pick any safe spot and direction away from Ultima.
        for (const dir of safeDir) {
          for (const run of [-1, 1]) {
            // If both directions were safe from Ultima, we would have found an early spot,
            // since one of them would be safe from Ifrit as well. So, not possible to
            // say "either direction" here, so just pick the first safe direction.
            const final = (dir + run + 8) % 8;
            if (final === ultimaDir)
              continue;
            const rotation = run === -1 ? output.counterclockwise!() : output.clockwise!();
            return output.normal!({ dir: dirStrMap[dir], rotation: rotation });
          }
        }
      },
      outputStrings: {
        early: {
          en: '${dir} => ${rotation} (early safe)',
          de: '${dir} => ${rotation} (früh sicher)',
          fr: '${dir} => ${rotation} (sûr avant)',
          ja: '${dir} => ${rotation} (先安地)',
          cn: '${dir} => ${rotation} (提前安全)',
          ko: '${dir} => ${rotation} (안전)',
          tc: '${dir} => ${rotation} (提前安全)',
        },
        normal: {
          en: '${dir} => ${rotation}',
          de: '${dir} => ${rotation}',
          fr: '${dir} => ${rotation}',
          ja: '${dir} => ${rotation}',
          cn: '${dir} => ${rotation}',
          ko: '${dir} => ${rotation}',
          tc: '${dir} => ${rotation}',
        },
        clockwise: Outputs.clockwise,
        counterclockwise: Outputs.counterclockwise,
        dirN: Outputs.dirN,
        dirE: Outputs.dirE,
        dirS: Outputs.dirS,
        dirW: Outputs.dirW,
      },
    },
    // --------- Suppression ----------
    {
      id: 'UWU Suppression Gaol',
      type: 'Ability',
      netRegex: { id: '2B6B', source: 'Titan' },
      condition: (data, matches) => data.phase === 'suppression' && data.me === matches.target,
      alarmText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Gaol on YOU',
          de: 'Granitgefängnis',
          fr: 'Geôle sur VOUS',
          ja: 'ジェイル',
          cn: '石牢点名',
          ko: '돌감옥 대상자',
          tc: '石牢點名',
        },
      },
    },
    {
      id: 'UWU Aetherochemical Laser Middle',
      type: 'StartsUsing',
      netRegex: { source: 'The Ultima Weapon', id: '2B84', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Middle Laser',
          de: 'Laser (Mitte)',
          fr: 'Laser (Milieu)',
          ja: 'レーザー (中央)',
          cn: '中间激光',
          ko: '가운데 레이저',
          tc: '中間雷射',
        },
      },
    },
    {
      id: 'UWU Aetherochemical Laser Right',
      type: 'StartsUsing',
      netRegex: { source: 'The Ultima Weapon', id: '2B85', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'North Laser',
          de: 'Laser (Norden)',
          fr: 'Laser (Nord)',
          ja: 'レーザー (北)',
          cn: '上半场激光',
          ko: '북쪽 레이저',
          tc: '北半場雷射',
        },
      },
    },
    {
      id: 'UWU Aetherochemical Laser Left',
      type: 'StartsUsing',
      netRegex: { source: 'The Ultima Weapon', id: '2B86', capture: false },
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'East Laser',
          de: 'Laser (Osten)',
          fr: 'Laser (Est)',
          ja: 'レーザー (東)',
          cn: '右半场激光',
          ko: '동쪽 레이저',
          tc: '東半場雷射',
        },
      },
    },
    // --------- Primal Roulette ----------
    {
      id: 'UWU Garuda Finale',
      type: 'Ability',
      netRegex: { source: 'The Ultima Weapon', id: '2CD3', capture: false },
      condition: (data) => data.phase === 'finale',
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Garuda',
          de: 'Garuda',
          fr: 'Garuda',
          ja: 'ガルーダ',
          cn: '迦楼罗',
          ko: '가루다',
          tc: '迦樓羅',
        },
      },
    },
    {
      id: 'UWU Ifrit Finale',
      type: 'Ability',
      netRegex: { source: 'The Ultima Weapon', id: '2CD4', capture: false },
      condition: (data) => data.phase === 'finale',
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Ifrit',
          de: 'Ifrit',
          fr: 'Ifrit',
          ja: 'イフリート',
          cn: '伊弗利特',
          ko: '이프리트',
          tc: '伊弗利特',
        },
      },
    },
    {
      id: 'UWU Titan Finale',
      type: 'Ability',
      netRegex: { source: 'The Ultima Weapon', id: '2CD5', capture: false },
      condition: (data) => data.phase === 'finale',
      infoText: (_data, _matches, output) => output.text!(),
      outputStrings: {
        text: {
          en: 'Titan',
          de: 'Titan',
          fr: 'Titan',
          ja: 'タイタン',
          cn: '泰坦',
          ko: '타이탄',
          tc: '泰坦',
        },
      },
    },
  ],
  timelineReplace: [
    {
      'locale': 'de',
      'replaceSync': {
        'Bomb Boulder': 'Bomber-Brocken',
        'Chirada': 'Chirada',
        'Garuda': 'Garuda',
        'Ifrit': 'Ifrit',
        'Lahabrea': 'Lahabrea',
        'Magitek Bit': 'Magitek-Drohne',
        'Spiny Plume': 'dornig(?:e|er|es|en) Federsturm',
        'Suparna': 'Suparna',
        'The Ultima Weapon': 'Ultima-Waffe',
        'Titan': 'Titan',
      },
      'replaceText': {
        'Aerial Blast': 'Windschlag',
        'Aetheric Boom': 'Ätherknall',
        'Aetherochemical Laser': 'Ätherochemischer Laser',
        '(?<! )Aetheroplasm': 'Ätheroplasma',
        'Apply Viscous': 'Ätheroplasma wirkt',
        'Blight': 'Pesthauch',
        'Bury': 'Begraben',
        'Ceruleum Vent': 'Erdseim-Entlüfter',
        'Citadel Siege': 'Belagerung der Zitadelle',
        'Crimson Cyclone': 'Zinnober-Zyklon',
        'Dark IV': 'Neka',
        'Diffractive Laser': 'Diffraktiver Laser',
        'Downburst': 'Fallböe',
        'Earthen Fury': 'Gaias Zorn',
        'Eruption': 'Eruption',
        'Eye Of The Storm': 'Auge des Sturms',
        'Feather Rain': 'Federregen',
        'Flaming Crush': 'Flammenstoß',
        'Freefire': 'Schwerer Beschuss',
        'Friction': 'Windklinge',
        'Geocrush': 'Geo-Stoß',
        'Great Whirlwind': 'Windhose',
        'Hellfire': 'Höllenfeuer',
        'Homing Lasers': 'Leitlaser',
        'Incinerate': 'Einäschern',
        'Infernal Fetters': 'Infernofesseln',
        'Inferno Howl': 'Brennende Wut',
        'Landslide': 'Bergsturz',
        'Light Pillar': 'Lichtsäule',
        'Mesohigh': 'Meso-Hoch',
        'Mistral Shriek': 'Mistral-Schrei',
        'Mistral Song': 'Mistral-Song',
        'Mountain Buster': 'Bergsprenger',
        'Nail Adds': 'Fessel Adds',
        'Radiant Plume': 'Scheiterhaufen',
        'Rock Buster': 'Steinsprenger',
        'Rock Throw': 'Granitgefängnis',
        'Searing Wind': 'Versengen',
        'Self-detonate': 'Zerbersten',
        'Slipstream': 'Wirbelströmung',
        'Summon Random Primal': 'Zufällige Primaebeschwörung',
        'Tank Purge': 'Tankreinigung',
        'Tumult': 'Urerschütterung',
        'Ultima(?!\\w)': 'Ultima',
        'Ultimate Annihilation': 'Ultimative Vernichtung',
        'Ultimate Predation': 'Ultimative Prädation',
        'Ultimate Suppression': 'Ultimative Unterdrückung',
        'Upheaval': 'Urtrauma',
        'Viscous Aetheroplasm': 'Viskoses Ätheroplasma',
        'Vulcan Burst': 'Feuerstoß',
        'Weight Of The Land': 'Gaias Gewicht',
        'Wicked Tornado': 'Tornado der Bosheit',
        'Wicked Wheel': 'Rad der Bosheit',
      },
    },
    {
      'locale': 'fr',
      'replaceSync': {
        'Bomb Boulder': 'bombo rocher',
        'Chirada': 'Chirada',
        'Garuda': 'Garuda',
        'Ifrit': 'Ifrit',
        'Lahabrea': 'Lahabrea',
        'Magitek Bit': 'drone magitek',
        'Spiny Plume': 'plume perforante',
        'Suparna': 'Suparna',
        'The Ultima Weapon': 'Ultima Arma',
        'Titan': 'Titan',
      },
      'replaceText': {
        'Aerial Blast': 'Rafale aérienne',
        'Aetheric Boom': 'Onde d\'éther',
        'Aetherochemical Laser': 'Laser magismologique',
        '(?<! )Aetheroplasm': 'Éthéroplasma',
        'Apply Viscous': 'Debuff Éthéroplasma',
        'Blight': 'Supplice',
        'Bury': 'Impact',
        'Ceruleum Vent': 'Exutoire à Céruleum',
        'Citadel Siege': 'Siège de citadelle',
        'Crimson Cyclone': 'Cyclone écarlate',
        'Dark IV': 'Giga Ténèbres',
        'Diffractive Laser': 'Laser diffractif',
        'Downburst': 'Rafale descendante',
        'Earthen Fury': 'Fureur tellurique',
        'Eruption': 'Éruption',
        'Eye Of The Storm': 'Œil du cyclone',
        'Feather Rain': 'Pluie de plumes',
        'Flaming Crush': 'Fracas de flammes',
        'Freefire': 'Tir d\'artillerie lourde',
        'Friction': 'Lame de vent',
        'Geocrush': 'Broie-terre',
        'Great Whirlwind': 'Grand tourbillon',
        'Hellfire': 'Flammes de l\'enfer',
        'Homing Lasers': 'Lasers autoguidés',
        'Incinerate': 'Incinération',
        'Infernal Fetters': 'Chaînes infernales',
        'Inferno Howl': 'Rugissement infernal',
        'Landslide': 'Glissement de terrain',
        'Light Pillar': 'Colonne lumineuse',
        'Mesohigh': 'Anticyclone de méso-échelle',
        'Mistral Shriek': 'Cri du mistral',
        'Mistral Song': 'Chant du mistral',
        'Mountain Buster': 'Casse-montagnes',
        'Nail Adds': 'Adds Clou',
        'Radiant Plume': 'Panache radiant',
        'Rock Buster': 'Casse-roc',
        'Rock Throw': 'Jeté de rocs',
        'Searing Wind': 'Carbonisation',
        'Self-detonate': 'Auto-atomisation',
        'Slipstream': 'Sillage',
        'Summon Random Primal': 'Invocation de primordial aléatoire',
        'Tank Purge': 'Vidange de réservoir',
        'Tumult': 'Tumulte',
        'Ultima(?!\\w)': 'Ultima',
        'Ultimate Annihilation': 'Fantasmagorie infernale',
        'Ultimate Predation': 'Fantasmagorie prédatrice',
        'Ultimate Suppression': 'Fantasmagorie bestiale',
        'Upheaval': 'Bouleversement',
        'Viscous Aetheroplasm': 'Éthéroplasma poisseux',
        'Vulcan Burst': 'Explosion volcanique',
        'Weight Of The Land': 'Poids de la terre',
        'Wicked Tornado': 'Tornade meurtrière',
        'Wicked Wheel': 'Roue mauvaise',
      },
    },
    {
      'locale': 'ja',
      'replaceSync': {
        'Bomb Boulder': 'ボムボルダー',
        'Chirada': 'チラーダ',
        'Garuda': 'ガルーダ',
        'Ifrit': 'イフリート',
        'Lahabrea': 'アシエン・ラハブレア',
        'Magitek Bit': '魔導ビット',
        'Spiny Plume': 'スパイニープルーム',
        'Suparna': 'スパルナ',
        'The Ultima Weapon': 'アルテマウェポン',
        'Titan': 'タイタン',
      },
      'replaceText': {
        'Aerial Blast': 'エリアルブラスト',
        'Aetheric Boom': 'エーテル波動',
        'Aetherochemical Laser': '魔科学レーザー',
        '(?<! )Aetheroplasm': 'エーテル爆雷',
        'Apply Viscous': '吸着式エーテル爆雷',
        'Blight': 'クラウダ',
        'Bury': '衝撃',
        'Ceruleum Vent': 'セルレアムベント',
        'Citadel Siege': 'シタデルシージ',
        'Crimson Cyclone': 'クリムゾンサイクロン',
        'Dark IV': 'ダージャ',
        'Diffractive Laser': '拡散レーザー',
        'Downburst': 'ダウンバースト',
        'Earthen Fury': '大地の怒り',
        'Eruption': 'エラプション',
        'Eye Of The Storm': 'アイ・オブ・ストーム',
        'Feather Rain': 'フェザーレイン',
        'Flaming Crush': 'フレイムクラッシュ',
        'Freefire': '誘爆',
        'Friction': 'ウィンドブレード',
        'Geocrush': 'ジオクラッシュ',
        'Great Whirlwind': '大旋風',
        'Hellfire': '地獄の火炎',
        'Homing Lasers': '誘導レーザー',
        'Incinerate': 'インシネレート',
        'Infernal Fetters': '炎獄の鎖',
        'Inferno Howl': '灼熱の咆吼',
        'Landslide': 'ランドスライド',
        'Light Pillar': 'リヒト・ゾイレ',
        'Mesohigh': 'メソハイ',
        'Mistral Shriek': 'ミストラルシュリーク',
        'Mistral Song': 'ミストラルソング',
        'Mountain Buster': 'マウンテンバスター',
        'Nail Adds': '雑魚: 楔',
        'Radiant Plume': '光輝の炎柱',
        'Rock Buster': 'ロックバスター',
        'Rock Throw': 'グラナイト・ジェイル',
        'Searing Wind': '熱風',
        'Self-detonate': '爆発霧散',
        'Slipstream': 'スリップストリーム',
        'Summon Random Primal': 'ランダム蛮神を召喚',
        'Tank Purge': '魔導フレア',
        'Tumult': '激震',
        'Ultima(?!\\w)': 'アルテマ',
        'Ultimate Annihilation': '爆撃の究極幻想',
        'Ultimate Predation': '追撃の究極幻想',
        'Ultimate Suppression': '乱撃の究極幻想',
        'Upheaval': '大激震',
        'Viscous Aetheroplasm': '吸着爆雷起爆',
        'Vulcan Burst': 'バルカンバースト',
        'Weight Of The Land': '大地の重み',
        'Wicked Tornado': 'ウィケッドトルネード',
        'Wicked Wheel': 'ウィケッドホイール',
      },
    },
    {
      'locale': 'cn',
      'replaceSync': {
        'Bomb Boulder': '爆破岩石',
        'Chirada': '妙翅',
        'Garuda': '迦楼罗',
        'Ifrit': '伊弗利特',
        'Lahabrea': '拉哈布雷亚',
        'Magitek Bit': '浮游炮射出',
        'Spiny Plume': '刺羽',
        'Suparna': '美翼',
        'The Ultima Weapon': '究极神兵',
        'Titan': '泰坦',
      },
      'replaceText': {
        'Aerial Blast': '大气爆发',
        'Aetheric Boom': '以太波动',
        'Aetherochemical Laser': '魔科学激光',
        '(?<! )Aetheroplasm': '以太爆雷',
        'Apply Viscous': '吸附式炸弹',
        'Blight': '毒雾',
        'Bury': '塌方',
        'Ceruleum Vent': '青磷放射',
        'Citadel Siege': '堡垒围攻',
        'Crimson Cyclone': '深红旋风',
        'Dark IV': '冥暗',
        'Diffractive Laser': '扩散射线',
        'Downburst': '下行突风',
        'Earthen Fury': '大地之怒',
        'Eruption': '地火喷发',
        'Eye Of The Storm': '台风眼',
        'Feather Rain': '飞翎雨',
        'Flaming Crush': '烈焰碎击',
        'Freefire': '诱导爆炸',
        'Friction': '烈风刃',
        'Geocrush': '大地粉碎',
        'Great Whirlwind': '大龙卷风',
        'Hellfire': '地狱之火炎',
        'Homing Lasers': '诱导射线',
        'Incinerate': '烈焰焚烧',
        'Infernal Fetters': '火狱之锁',
        'Inferno Howl': '灼热咆哮',
        'Landslide': '地裂',
        'Light Pillar': '光柱',
        'Mesohigh': '中高压',
        'Mistral Shriek': '寒风之啸',
        'Mistral Song': '寒风之歌',
        'Mountain Buster': '山崩',
        'Nail Adds': '火神柱',
        'Radiant Plume': '光辉炎柱',
        'Rock Buster': '碎岩',
        'Rock Throw': '花岗岩牢狱',
        'Searing Wind': '灼热',
        'Self-detonate': '雾散爆发',
        'Slipstream': '螺旋气流',
        'Summon Random Primal': '召唤随机蛮神',
        'Tank Purge': '魔导核爆',
        'Tumult': '怒震',
        'Ultima(?!\\w)': '究极',
        'Ultimate Annihilation': '爆击之究极幻想',
        'Ultimate Predation': '追击之究极幻想',
        'Ultimate Suppression': '乱击之究极幻想',
        'Upheaval': '大怒震',
        'Viscous Aetheroplasm': '引爆吸附式炸弹',
        'Vulcan Burst': '火神爆裂',
        'Weight Of The Land': '大地之重',
        'Wicked Tornado': '邪气龙卷',
        'Wicked Wheel': '邪轮旋风',
      },
    },
    {
      'locale': 'tc',
      'replaceSync': {
        'Bomb Boulder': '爆破岩石',
        'Chirada': '妙翅',
        'Garuda': '迦樓羅',
        'Ifrit': '伊弗利特',
        'Lahabrea': '拉哈布雷亞',
        'Magitek Bit': '浮游砲射出',
        'Spiny Plume': '刺羽',
        'Suparna': '美翼',
        'The Ultima Weapon': '究極武器',
        'Titan': '泰坦',
      },
      'replaceText': {
        'Aerial Blast': '大氣爆發',
        'Aetheric Boom': '乙太波動',
        'Aetherochemical Laser': '魔科學雷射',
        '(?<! )Aetheroplasm': '乙太爆雷',
        'Apply Viscous': '吸附式炸彈',
        'Blight': '毒霧',
        'Bury': '衝擊',
        'Ceruleum Vent': '青磷放射',
        'Citadel Siege': '堡壘圍攻',
        'Crimson Cyclone': '深紅旋風',
        'Dark IV': '冥暗',
        'Diffractive Laser': '擴散雷射',
        'Downburst': '下行突風',
        'Earthen Fury': '大地之怒',
        'Eruption': '噴發',
        'Eye Of The Storm': '颱風眼',
        'Feather Rain': '飛翎雨',
        'Flaming Crush': '烈焰碎擊',
        'Freefire': '誘導爆炸',
        'Friction': '烈風刃',
        'Geocrush': '大地撞擊',
        'Great Whirlwind': '大旋風',
        'Hellfire': '地獄之火炎',
        'Homing Lasers': '誘導射線',
        'Incinerate': '烈焰焚燒',
        'Infernal Fetters': '火獄之鎖',
        'Inferno Howl': '灼熱咆哮',
        'Landslide': '地裂',
        'Light Pillar': '光柱',
        'Mesohigh': '中高壓',
        'Mistral Shriek': '寒風之嘯',
        'Mistral Song': '寒風之歌',
        'Mountain Buster': '山崩',
        'Nail Adds': '火神柱',
        'Radiant Plume': '光輝炎柱',
        'Rock Buster': '岩石破壞者',
        'Rock Throw': '花崗岩牢獄',
        'Searing Wind': '灼熱',
        'Self-detonate': '霧散爆發',
        'Slipstream': '螺旋氣流',
        'Summon Random Primal': '召喚隨機蠻神',
        'Tank Purge': '魔導火光',
        'Tumult': '激震',
        'Ultima(?!\\w)': '最終究極',
        'Ultimate Annihilation': '爆擊之究極幻想',
        'Ultimate Predation': '追擊之究極幻想',
        'Ultimate Suppression': '亂擊之究極幻想',
        'Upheaval': '大激震',
        'Viscous Aetheroplasm': '引爆吸附式炸彈',
        'Vulcan Burst': '火神爆裂',
        'Weight Of The Land': '大地重壓',
        'Wicked Tornado': '邪氣龍捲',
        'Wicked Wheel': '邪輪旋風',
      },
    },
    {
      'locale': 'ko',
      'replaceSync': {
        'Bomb Boulder': '바위폭탄',
        'Chirada': '치라다',
        'Garuda': '가루다',
        'Ifrit': '이프리트',
        'Lahabrea': '아씨엔 라하브레아',
        'Magitek Bit': '마도 비트',
        'Spiny Plume': '가시돋힌 깃털',
        'Suparna': '수파르나',
        'The Ultima Weapon': '알테마 웨폰',
        'Titan': '타이탄',
      },
      'replaceText': {
        'Aerial Blast': '대기 폭발',
        'Aetheric Boom': '에테르 파동',
        'Aetherochemical Laser': '마과학 레이저',
        '(?<! )Aetheroplasm': '에테르 폭뢰',
        'Apply Viscous': '흡착식 에테르 폭뢰',
        'Blight': '독안개',
        'Bury': '충격',
        'Ceruleum Vent': '청린 방출',
        'Citadel Siege': '공성',
        'Crimson Cyclone': '진홍 회오리',
        'Dark IV': '다쟈',
        'Diffractive Laser': '확산 레이저',
        'Downburst': '하강 기류',
        'Earthen Fury': '대지의 분노',
        'Eruption': '용암 분출',
        'Eye Of The Storm': '태풍의 눈',
        'Feather Rain': '깃털비',
        'Flaming Crush': '화염 작열',
        'Freefire': '유폭',
        'Friction': '바람의 칼날',
        'Geocrush': '대지 붕괴',
        'Great Whirlwind': '대선풍',
        'Hellfire': '지옥의 화염',
        'Homing Lasers': '유도 레이저',
        'Incinerate': '소각',
        'Infernal Fetters': '염옥의 사슬',
        'Inferno Howl': '작열의 포효',
        'Landslide': '산사태',
        'Light Pillar': '빛 기둥',
        'Mesohigh': '뇌우고기압',
        'Mistral Shriek': '삭풍의 비명',
        'Mistral Song': '삭풍의 노래',
        'Mountain Buster': '산 쪼개기',
        'Nail Adds': '염옥의 말뚝',
        'Radiant Plume': '광휘의 불기둥',
        'Rock Buster': '바위 쪼개기',
        'Rock Throw': '화강암 감옥',
        'Searing Wind': '열풍',
        'Self-detonate': '자가폭발',
        'Slipstream': '반동 기류',
        'Summon Random Primal': '무작위 야만신 소환',
        'Tank Purge': '마도 플레어',
        'Tumult': '격진',
        'Ultima(?!\\w)': '알테마',
        'Ultimate Annihilation': '궁극의 폭격 환상',
        'Ultimate Predation': '궁극의 추격 환상',
        'Ultimate Suppression': '궁극의 난격 환상',
        'Upheaval': '대격진',
        'Viscous Aetheroplasm': '흡착 폭뢰 기폭',
        'Vulcan Burst': '폭렬 난사',
        'Weight Of The Land': '대지의 무게',
        'Wicked Tornado': '마녀의 회오리',
        'Wicked Wheel': '마녀의 수레바퀴',
      },
    },
  ],
};

export default triggerSet;
